"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;
    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);
      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38]  border-b border-[#b08d38] bg-white  " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;
        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300  ">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white  transition-transform duration-300 text-[#000]" +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%]  text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Prime Location in TVS Nagar",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            TVS Nagar is one of the prime locations in Coimbatore. It connects
            excellently with Avinashi Road, NH-544, and Coimbatore Bypass. The
            schools, hospitals, shopping malls, and airport are all within a
            short distance from the locality. The area offers a scenic view of
            the Western Ghats. This place is very convenient for daily easy
            living and adds value to your investment in the long run.
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: "Premium Infrastructure",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            TVS Nagar has modern infrastructure and amenities that enhance the
            living experience
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Wide internal roads and well-planned layouts",
              "The property includes underground drainage and cabling, contributing to a clean aesthetic landscape.",
              "24/7 security and gated community features.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            These features not only enhance comfort but also boost resale and
            rental value.
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: "A serene living environment",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            TVS Nagar is then equipped with such a peaceful setting that
            PlusWilla adds to it with its greening and vastu-compliant features.
            The serene surroundings within the gated community of Chennakode and
            TVS Nagar are similar to lush gardens with an open layout system
            inside, along with an east-west-facing vastu architecture. Low noise
            and pollution levels are ideal for families and retirees. Green
            zones and walking paths within the community. The architecture
            reflects harmony with nature and incorporates traditional
            principles.{" "}
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: "Access to Top-Tier Amenities",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            PlusWilla developments are surrounded by basic amenities and
            lifestyle conveniences. The area is home to educational institutions
            such as CS Academy and PSG Public School. The area also includes
            healthcare facilities, such as Ganga Hospital and KMCH. Shopping and
            entertainment at Brookefields Mall and local markets. This is why
            PlusWilla villas are suited for individuals who seek calm in
            modernity.
          </p>
        </>
      ),
    },
    {
      id: 5,
      title: "Strong Investment Potential",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            The real estate of TVS Nagar has always been an appreciated space.
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "High demand for independent homes in Coimbatore.",
              "Rental income potential from villas and duplexes.",
              "We expect infrastructure improvements and commercial expansion to drive future growth.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Investing in PlusWilla plots allows you to build custom homes that
            match market trends and buyer preferences.
          </p>
        </>
      ),
    },

    {
      id: 6,
      title: "Customization and Flexibility",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            One of the biggest advantages of investing in PlusWilla plots is the
            freedom to design your dream home
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Vastu-compliant plots for traditional buyers.",
              "Architectural support for modern and smart home designs.",
              "Positive energy environments for sustainable living.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Usually, these features of the area attract buyers who are
            interested in opening up their homes for personal improvement.
          </p>
        </>
      ),
    },

    {
      id: 7,
      title: "Lifestyle Perks",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            Residing in a PlusWilla community promotes a vibrant lifestyle.
            PlusWilla designs these communities to foster health, fitness, and
            social interaction.
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Clubhouses and gyms",
              "Children's play areas and jogging tracks",
              "Community fairs and cultural halls",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            All of them contribute to creating a lively and serene ambiance in
            the community.
          </p>
        </>
      ),
    },

    {
      id: 8,
      title: "Safety and security",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            We ensure your safety with top security measures
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Gated communities with controlled access.",
              "CCTV surveillance and security.",
              "Streets with light and emergency response systems.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This makes PlusWilla villas ideal for families, seniors, and solo
            residents.
          </p>
        </>
      ),
    },

    {
      id: 9,
      title: "Transparent and Trustworthy Dealings",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            PlusWilla is reputed for its transparency in business practices and
            customer-first approach.
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "All the projects we take over have RERA (Real Estate Regulatory Authority)",
              "The documentation and transactions are effortless.",
              "Customer support is fully committed during the purchase transactions.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Thus, a sense of ease and trust is created among investors as well
            as residential customers.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};

export default function TopReasonsToInvest() {
  const data = [
    {
      image: `/images/blogs/top-reasons-to-invest-in-villas-plots-in-tvs-nagar.webp`,
      title: "Top Reasons to Invest in Villas & Plots in TVS Nagar",
      date: "2 Nov 2025",

      content1: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            TVS Nagar, the central region of Coimbatore, is rapidly transforming
            into a luxury residential enclave. PlusWilla, one of the most
            prestigious projects in the district, is redefining luxury living
            with villas and carefully crafted plots. If you've been actively
            looking into real estate investment opportunities in Coimbatore,
            this blog will guide you to consider PlusWilla at TVS Nagar.
          </p>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Here are insights into why PlusWilla villas and plots rank as prime
            investment choices for the year 2025.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="sticky top-[130px] z-[2] ">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog list"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px] ">
            <p className="my-[20px] text-[20px] leading-[28px] font-light max-xxl:text-[18px] max-xxl:leading-[26px] group-hover:text-[#8e722d] transition-colors duration-300 max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>

          <div className="p-[30px] bg-[#e8e8e8] rounded-[30px] mt-[50px]">
            <h2 className="text-[22px] leading-[30px] md:text-[24px] md:leading-[32px] 2xl:text-[26px] 2xl:leading-[34px] font-medium text-gray-900 mb-[15px]">
              Conclusion
            </h2>
            <p className="mb-[15px]">
              To purchase luxury villas and plots in TVS Nagar through PlusWilla
              is to buy a lifestyle, future, and legacy.
            </p>
            <p className="mb-[15px]">
              PlusWilla is an excellent choice that combines luxury and utility
              through its prime location, high-quality infrastructure, and
              strong investment potential. Choosing PlusWilla at TVS Nagar would
              be a decision that you would never regret, whether it becomes your
              forever home or your latest real estate investment.
            </p>
            <p className="mb-[15px]">
              Now that you're excited to check what PlusWilla is offering, let's
              jump into the available plot sizes, pricing options, and potential
              customizations!
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
